import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { X } from 'lucide-react';
import { User } from '../../../types';

interface Contact {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  avatar?: string;
  type: 'internal' | 'external';
}

interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (contactData: Partial<Contact>) => void;
  contact: Contact | null;
}

export const ContactDialog: React.FC<ContactDialogProps> = ({
  open,
  onClose,
  onSave,
  contact,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    phone: '',
    type: 'external' as const,
    avatar: '',
  });

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name,
        email: contact.email,
        role: contact.role,
        phone: contact.phone,
        type: contact.type,
        avatar: contact.avatar || '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        role: '',
        phone: '',
        type: 'external',
        avatar: '',
      });
    }
  }, [contact]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          m: isMobile ? 2 : 3,
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          <Typography variant="h6" component="div">
            {contact ? 'Edit Contact' : 'Add Contact'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {formData.avatar && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Avatar
                  src={formData.avatar}
                  alt={formData.name}
                  sx={{ width: 80, height: 80 }}
                />
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Contact Type</InputLabel>
              <Select
                value={formData.type}
                label="Contact Type"
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'internal' | 'external' }))}
              >
                <MenuItem value="internal">Staff</MenuItem>
                <MenuItem value="external">External Contact</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            />

            <TextField
              label="Role"
              fullWidth
              required
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
            />

            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            />

            <TextField
              label="Phone"
              fullWidth
              required
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              inputProps={{
                inputMode: 'tel',
              }}
            />

            <TextField
              label="Avatar URL"
              fullWidth
              value={formData.avatar}
              onChange={(e) => setFormData(prev => ({ ...prev, avatar: e.target.value }))}
              helperText="Enter a URL for the contact's avatar image"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ 
          px: 3, 
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
        }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth={isMobile}
          >
            {contact ? 'Save Changes' : 'Add Contact'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};